import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { ROUTES } from '@common/constants/routes'
import { IAtlasObjectRef, parseAtlasObjectUrl } from '@common/helpers/atlasUrls'
import { atlasObjectRefKey } from '@common/interfaces/atlasObjects'
import { usePermissions } from '../helpers/permissionContext'
import { useSelector } from '../redux/hooks'
import { isAdminLoggedAsClient } from '../redux/profile/selectors'
import { useOpenAtlasPreview } from './useOpenAtlasPreview'

/** Spread on a surface rendered outside AIM and ACT pages whose record links should preview. */
export const ATLAS_PREVIEW_ZONE = { 'data-atlas-preview': 'on' } as const

/** Spread on app chrome (menus, header) whose links must navigate even on AIM and ACT pages. */
export const ATLAS_PREVIEW_OPT_OUT = { 'data-atlas-preview': 'off' } as const

/** Spread on a link that opens its own preview (a chip), so this listener leaves it to the chip. */
export const ATLAS_PREVIEW_TRIGGER = { 'data-atlas-preview': 'trigger' } as const

const ZONE_SELECTOR = '[data-atlas-preview="on"]'
const TRIGGER_SELECTOR = '[data-atlas-preview="trigger"]'
const EXCLUDED_SELECTOR = '[data-atlas-preview="off"], .MuiModal-root, .MuiPopper-root'

// AIM and ACT: where a record link is a reference to glance at. Everywhere else
// (dashboards, lists) a record link is navigation and keeps navigating.
const ZONE_ROUTES = [
  ROUTES.MESSAGES,
  `${ROUTES.MESSAGES}/*`,
  ROUTES.MY_TASKS,
  ROUTES.TASK_PROJECT_PAGE,
]

const isZoneRoute = (pathname: string): boolean =>
  ZONE_ROUTES.some((pattern) => !!matchPath(pattern, pathname))

const isPlainPrimaryClick = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.defaultPrevented &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey

const findRecordLink = (
  event: MouseEvent,
  isOnZoneRoute: boolean,
): { ref: IAtlasObjectRef; url: string; anchor: HTMLAnchorElement } | null => {
  const anchor = (event.target as Element | null)?.closest?.('a[href]')
  if (!(anchor instanceof HTMLAnchorElement)) return null
  if (anchor.target === '_blank' || anchor.hasAttribute('download')) return null
  if (anchor.matches(TRIGGER_SELECTOR)) return null
  if (anchor.closest(EXCLUDED_SELECTOR)) return null
  if (!isOnZoneRoute && !anchor.closest(ZONE_SELECTOR)) return null

  const url = anchor.getAttribute('href') ?? ''
  const ref = parseAtlasObjectUrl(url)
  return ref ? { ref, url, anchor } : null
}

/**
 * Opens record links in the preview card inside AIM and ACT: their pages, plus the AIM
 * bubble and the task panel wherever those render. Elsewhere links keep navigating.
 *
 * Runs in the capture phase so a RouterLink, or a row click handler around the link, never
 * sees the click. A link to the record already on screen still navigates. Off for roles the
 * preview endpoint doesn't serve.
 */
export const useGlobalAtlasLinkPreview = () => {
  const { isMessagingUser } = usePermissions()
  const isLoggedAsClient = useSelector(isAdminLoggedAsClient)
  const openPreview = useOpenAtlasPreview()
  const { pathname, search } = useLocation()
  const isEnabled = isMessagingUser && !isLoggedAsClient
  const isOnZoneRoute = isZoneRoute(pathname)

  // Both the page's record and an open task panel's: `/projects/A?taskId=T` is on A and T.
  const currentRecordKeys = [
    parseAtlasObjectUrl(`${pathname}${search}`),
    parseAtlasObjectUrl(pathname),
  ]
    .filter((ref): ref is IAtlasObjectRef => !!ref)
    .map(atlasObjectRefKey)
    .join(',')

  useEffect(() => {
    if (!isEnabled) return

    const handleClick = (event: MouseEvent) => {
      if (!isPlainPrimaryClick(event)) return
      const link = findRecordLink(event, isOnZoneRoute)
      if (!link || currentRecordKeys.split(',').includes(atlasObjectRefKey(link.ref))) return

      event.preventDefault()
      event.stopPropagation()
      openPreview(link.ref, link.url, link.anchor)
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [isEnabled, isOnZoneRoute, openPreview, currentRecordKeys])
}
